/**
 * InterviewSession — lean, focused view for a single interview round.
 *
 * Intentionally minimal so the interviewer can run an unbiased assessment:
 *   • NO candidate contact info (email, phone, linkedin)
 *   • NO AI scoring, no recruiter notes, no other interviewers' feedback
 *   • NO access to other candidates, offers, salary, screening profile
 *
 * What the interviewer DOES see:
 *   1. Candidate name, current title/company, total experience
 *   2. Round name + scheduled time + mode + meet link
 *   3. The candidate's latest resume (inline PDF)
 *   4. Interview Q&A — answer + score each question
 *   5. Rating panel (hard skills + soft skills + critical + overall)
 *   6. Feedback text + outcome + status
 *
 * Endpoints used:
 *   GET   /api/interviewer/interview/{iid}
 *   PATCH /api/interviewer/interview/{iid}/feedback
 *   POST  /api/interviewer/interview/{iid}/qa
 *   PATCH /api/interviewer/interview/{iid}/qa/{qid}
 *   POST  /api/interviewer/interview/{iid}/rating
 *   GET   /api/interviewer/interview/{iid}/resume
 */
import { useEffect, useMemo, useRef, useState, memo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
    ArrowLeft, Briefcase, Clock, FileText, MessageSquare, Star,
    CheckCircle2, LogOut, User2, Plus, Loader2, Video, Phone, Building2,
    ExternalLink, Save, Maximize2, Lock, Sparkles, StickyNote,
} from "lucide-react";

const OUTCOMES = ["Pending", "Strong Hire", "Hire", "Lean Hire", "Lean No", "No Hire", "Pass", "Fail"];
const STATUSES = ["Scheduled", "Completed", "No-show", "Cancelled", "Rescheduled"];
const DEFAULT_CRITICAL = ["Culture fit", "Communication", "Problem solving", "Ownership", "Domain depth"];

function fmtTime(iso, dateOnly) {
    if (!iso) return "—";
    try {
        if (dateOnly) {
            const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
            const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
            return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
        }
        return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

function ModeIcon({ mode }) {
    if (mode === "Phone") return <Phone className="w-3.5 h-3.5" />;
    if (mode === "Onsite") return <Building2 className="w-3.5 h-3.5" />;
    return <Video className="w-3.5 h-3.5" />;
}

// ── 1-5 star rating row ─────────────────────────────────────────────────
function StarRow({ value, onChange, disabled }) {
    return (
        <div className="inline-flex items-center gap-0.5" data-testid="star-row">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    onClick={() => !disabled && onChange(value === n ? null : n)}
                    disabled={disabled}
                    className={`p-0.5 ${n <= (value || 0) ? "text-amber-500" : "text-foreground/20 hover:text-foreground/40"} disabled:cursor-not-allowed`}
                    data-testid={`star-${n}`}
                >
                    <Star className="w-4 h-4" fill={n <= (value || 0) ? "currentColor" : "none"} />
                </button>
            ))}
        </div>
    );
}

function QACard({ q, onSave }) {
    const [answer, setAnswer] = useState(q.answer || "");
    const [score, setScore] = useState(q.score ?? "");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setAnswer(q.answer || ""); setScore(q.score ?? ""); }, [q.id, q.answer, q.score]);

    const dirty = (answer !== (q.answer || "")) || ((score === "" ? null : Number(score)) !== q.score);

    const save = async () => {
        setSaving(true);
        try {
            await onSave(q.id, {
                answer,
                score: score === "" ? null : Math.max(1, Math.min(10, Number(score))),
            });
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-border bg-white p-4" data-testid={`qa-${q.id}`}>
            <div className="flex items-start gap-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mt-0.5 shrink-0">
                    Q{(q.order ?? 0) + 1}
                </span>
                <p className="font-display font-bold text-sm leading-snug flex-1">{q.question}</p>
                {q.source === "ai" && (
                    <span className="text-[9px] font-mono uppercase tracking-widest bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0.5">AI</span>
                )}
            </div>
            <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type the candidate's response here…"
                className="rounded-none mt-3 text-sm min-h-[90px]"
                data-testid={`qa-${q.id}-answer`}
            />
            <div className="flex items-center gap-3 mt-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Score (1–10)</span>
                    <Input
                        type="number"
                        min={1}
                        max={10}
                        value={score}
                        onChange={(e) => setScore(e.target.value)}
                        placeholder="—"
                        className="rounded-none h-8 w-20 text-sm text-right font-mono"
                        data-testid={`qa-${q.id}-score`}
                    />
                </div>
                {dirty && (
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="ml-auto rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        data-testid={`qa-${q.id}-save`}
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        {saving ? "Saving…" : "Save"}
                    </Button>
                )}
            </div>
        </div>
    );
}

function RatingPanel({ skills, rating, onSave }) {
    // skills: { hard: ["Python", ...], soft: [...] } — derived from job + candidate
    const [hard, setHard] = useState({});
    const [soft, setSoft] = useState({});
    const [critical, setCritical] = useState({});
    const [overall, setOverall] = useState("");
    const [recommendation, setRecommendation] = useState("");
    const [summary, setSummary] = useState("");
    const [saving, setSaving] = useState(false);
    const [customSkill, setCustomSkill] = useState("");

    useEffect(() => {
        setHard(rating?.hard_skills || {});
        setSoft(rating?.soft_skills || {});
        setCritical(rating?.critical || {});
        setOverall(rating?.overall ?? "");
        setRecommendation(rating?.recommendation || "");
        setSummary(rating?.summary || "");
    }, [rating?.id, rating?.updated_at]);

    const allHardKeys = useMemo(() => {
        const fromSkills = (skills.hard || []).map((s) => s);
        const fromRating = Object.keys(hard);
        return Array.from(new Set([...fromSkills, ...fromRating]));
    }, [skills.hard, hard]);

    const softList = useMemo(() => {
        // Prefer the recruiter-pushed soft skills list; fall back to defaults.
        const pushed = (skills.soft || []).filter(Boolean);
        return pushed.length ? pushed : ["Communication", "Collaboration", "Ownership", "Initiative"];
    }, [skills.soft]);
    const criticalList = useMemo(() => {
        const pushed = (skills.critical || []).filter(Boolean);
        return pushed.length ? pushed : DEFAULT_CRITICAL;
    }, [skills.critical]);

    const save = async () => {
        setSaving(true);
        try {
            await onSave({
                hard_skills: hard,
                soft_skills: soft,
                critical,
                overall: overall === "" ? null : Math.max(1, Math.min(10, Number(overall))),
                recommendation: recommendation || null,
                summary: summary || null,
            });
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-border bg-white" data-testid="rating-panel">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-700" />
                <h3 className="font-display font-bold text-base text-amber-800">Round rating</h3>
                <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700/70 ml-auto">Only you can see your rating</span>
            </div>
            <div className="p-4 space-y-5">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Hard skills</div>
                    <div className="space-y-2">
                        {allHardKeys.length === 0 && <div className="text-xs text-foreground/40">No hard skills tagged — add one below.</div>}
                        {allHardKeys.map((k) => (
                            <div key={k} className="flex items-center justify-between gap-3" data-testid={`rate-hard-${k}`}>
                                <span className="text-sm">{k}</span>
                                <StarRow value={hard[k]} onChange={(v) => setHard({ ...hard, [k]: v })} />
                            </div>
                        ))}
                        <div className="flex items-center gap-2 pt-2">
                            <Input
                                value={customSkill}
                                onChange={(e) => setCustomSkill(e.target.value)}
                                placeholder="Add a hard skill to rate…"
                                className="rounded-none h-8 text-sm flex-1"
                                data-testid="rate-add-skill-input"
                            />
                            <Button
                                onClick={() => {
                                    const s = customSkill.trim();
                                    if (s && !hard[s]) {
                                        setHard({ ...hard, [s]: null });
                                        setCustomSkill("");
                                    }
                                }}
                                disabled={!customSkill.trim()}
                                variant="outline"
                                className="rounded-none h-8 text-xs"
                                data-testid="rate-add-skill-btn"
                            >
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add
                            </Button>
                        </div>
                    </div>
                </div>

                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Soft skills</div>
                    <div className="space-y-2">
                        {softList.map((k) => (
                            <div key={k} className="flex items-center justify-between gap-3" data-testid={`rate-soft-${k}`}>
                                <span className="text-sm">{k}</span>
                                <StarRow value={soft[k]} onChange={(v) => setSoft({ ...soft, [k]: v })} />
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Critical parameters</div>
                    <div className="space-y-2">
                        {criticalList.map((k) => (
                            <div key={k} className="flex items-center justify-between gap-3" data-testid={`rate-critical-${k}`}>
                                <span className="text-sm">{k}</span>
                                <StarRow value={critical[k]} onChange={(v) => setCritical({ ...critical, [k]: v })} />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border-t border-border pt-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Overall (1–10)</label>
                            <Input type="number" min={1} max={10} value={overall} onChange={(e) => setOverall(e.target.value)} className="rounded-none h-9 mt-1" data-testid="rate-overall" />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Recommendation</label>
                            <select value={recommendation} onChange={(e) => setRecommendation(e.target.value)} className="w-full h-9 mt-1 border border-border bg-white text-sm px-2" data-testid="rate-recommendation">
                                <option value="">—</option>
                                {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Summary</label>
                        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Top-line summary of the round…" className="rounded-none mt-1 min-h-[60px] text-sm" data-testid="rate-summary" />
                    </div>
                    <Button onClick={save} disabled={saving} className="w-full rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="rate-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        {saving ? "Saving rating…" : "Save rating"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function FeedbackBlock({ interview, onSave }) {
    const [feedback, setFeedback] = useState(interview.feedback || "");
    const [score, setScore] = useState(interview.score ?? "");
    const [outcome, setOutcome] = useState(interview.outcome || "");
    const [status, setStatus] = useState(interview.status || "Scheduled");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setFeedback(interview.feedback || "");
        setScore(interview.score ?? "");
        setOutcome(interview.outcome || "");
        setStatus(interview.status || "Scheduled");
    }, [interview.id, interview.updated_at]);

    const save = async () => {
        setSaving(true);
        try {
            await onSave({
                feedback: feedback || null,
                score: score === "" ? null : Math.max(1, Math.min(10, Number(score))),
                outcome: outcome || null,
                status,
            });
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-border bg-white" data-testid="feedback-block">
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                <h3 className="font-display font-bold text-base text-emerald-800">Round verdict & feedback</h3>
            </div>
            <div className="p-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Status</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-9 mt-1 border border-border bg-white text-sm px-2" data-testid="fb-status">
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Outcome</label>
                        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="w-full h-9 mt-1 border border-border bg-white text-sm px-2" data-testid="fb-outcome">
                            <option value="">—</option>
                            {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Score (1–10)</label>
                        <Input type="number" min={1} max={10} value={score} onChange={(e) => setScore(e.target.value)} className="rounded-none h-9 mt-1" data-testid="fb-score" />
                    </div>
                </div>
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Feedback</label>
                    <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Detailed feedback on the round — strengths, weaknesses, suggested probes…" className="rounded-none mt-1 min-h-[140px] text-sm" data-testid="fb-text" />
                </div>
                <Button onClick={save} disabled={saving} className="w-full rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="fb-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {saving ? "Saving feedback…" : "Save feedback"}
                </Button>
            </div>
        </div>
    );
}

/**
 * ResumePanel — memoized so it does NOT re-mount when sibling state
 * (tab toggle, ratings save, Q&A save) triggers re-renders on the parent.
 *
 * Renders the MASKED resume (contact info, social links, exact location and
 * surname redacted) as inline HTML in the left panel. Interviewers see the
 * candidate's original wording verbatim but without identifying info — gives
 * them a bias-free read without the risk of accidentally reaching out
 * directly. No download/print affordance for the interviewer.
 *
 * The HTML is rendered into a sandboxed `<iframe srcDoc>` so:
 *   - it's visually identical to the recruiter's printable version
 *   - the iframe sandbox blocks scripts, top-nav, popups, forms
 *   - print/save actions go through the iframe's own context (none provided)
 *
 * Auth: standard cookie auth via the api client.
 */
const ResumePanel = memo(function ResumePanel({ iid, filename, hasResume, resumeType }) {
    const wrapRef = useRef(null);
    const [maskedHtml, setMaskedHtml] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const showOriginal = resumeType === "original";

    useEffect(() => {
        if (!iid || !hasResume) {
            setMaskedHtml(null);
            return undefined;
        }
        if (showOriginal) {
            // Original resume is served via /resume (PDF iframe path). We
            // don't need to pre-fetch HTML in that case.
            setMaskedHtml(null);
            setLoading(false);
            setErr(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true); setErr(null);
        (async () => {
            try {
                const res = await api.get(`/interviewer/interview/${iid}/masked-resume`, {
                    responseType: "text",
                });
                if (cancelled) return;
                setMaskedHtml(typeof res.data === "string" ? res.data : "");
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [iid, hasResume, showOriginal]);

    const goFullScreen = () => {
        const el = wrapRef.current;
        if (!el) return;
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el);
        }
    };

    return (
        <div ref={wrapRef} className="border border-border bg-white relative" data-testid="resume-panel">
            <div className="px-4 py-3 bg-foreground text-background flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <h3 className="font-display font-bold text-base">Candidate resume</h3>
                <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest opacity-70" title={showOriginal ? "Admin has unlocked the original resume for your account" : "Contact info, social links, and surname are masked for a bias-free assessment"}>
                    <Lock className="w-3 h-3" /> {showOriginal ? "Original · Read-only" : "Masked · Read-only"}
                </span>
                {filename && <span className="text-[10px] font-mono uppercase tracking-widest opacity-70 ml-2 truncate">{filename}</span>}
                {hasResume && maskedHtml && (
                    <button
                        onClick={goFullScreen}
                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-white/30 hover:bg-white/10 px-2 py-1"
                        title="Toggle full screen"
                        data-testid="resume-fullscreen"
                    >
                        <Maximize2 className="w-3 h-3" /> Full screen
                    </button>
                )}
            </div>
            {!hasResume ? (
                <div className="p-10 text-center text-sm text-foreground/60">
                    No resume on file for this candidate.
                </div>
            ) : showOriginal ? (
                <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/interviewer/interview/${iid}/resume#toolbar=0`}
                    title="Original candidate resume"
                    className="w-full h-[760px] bg-white"
                    data-testid="resume-iframe"
                />
            ) : loading ? (
                <div className="p-10 text-center text-sm text-foreground/60 inline-flex items-center justify-center gap-2 w-full">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading resume…
                </div>
            ) : err ? (
                <div className="p-10 text-center text-sm text-rose-700 bg-rose-50 border-t border-rose-200" data-testid="resume-error">
                    Could not load resume: {err}
                </div>
            ) : (
                <iframe
                    srcDoc={maskedHtml}
                    title="Masked candidate resume"
                    className="w-full h-[760px] bg-white"
                    sandbox="allow-same-origin"
                    data-testid="resume-iframe"
                />
            )}
        </div>
    );
}, (prev, next) => (
    prev.iid === next.iid
    && prev.filename === next.filename
    && prev.hasResume === next.hasResume
    && prev.resumeType === next.resumeType
));

function InterviewerNotesPanel({ iid }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/interviewer/interview/${iid}/my-notes`);
            setNotes(Array.isArray(data) ? data : []);
        } catch (err) {
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [iid]);

    const addNote = async () => {
        if (!body.trim()) return;
        setBusy(true);
        try {
            await api.post(`/interviewer/interview/${iid}/note`, { body: body.trim() });
            setBody("");
            await load();
            toast.success("Note added — recruiter notified");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3" data-testid="interviewer-notes-panel">
            <div className="border border-border bg-white p-3" data-testid="iv-note-add">
                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-2">
                    Add a note on the candidate
                </div>
                <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Jot down what stood out, concerns, follow-up topics… The recruiter will see this in the candidate's notes."
                    rows={4}
                    className="rounded-none text-sm"
                    data-testid="iv-note-body"
                />
                <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="text-[10px] font-mono text-foreground/50">{body.length} / 4000</span>
                    <Button
                        onClick={addNote}
                        disabled={busy || !body.trim()}
                        className="rounded-none h-9 bg-ikb hover:bg-[#001f7a] text-white"
                        data-testid="iv-note-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        {busy ? "Saving…" : "Add note"}
                    </Button>
                </div>
            </div>
            <div className="space-y-2" data-testid="iv-note-list">
                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                    Your notes on this candidate
                </div>
                {loading && <div className="text-xs font-mono text-foreground/60">Loading…</div>}
                {!loading && notes.length === 0 && (
                    <div className="border border-dashed border-border bg-white p-6 text-center text-sm text-foreground/60" data-testid="iv-note-empty">
                        No notes yet. Add one above — it will append to the recruiter's notes.
                    </div>
                )}
                {!loading && notes.map((n) => (
                    <div key={n.id} className="border border-border bg-white p-3" data-testid={`iv-note-${n.id}`}>
                        <div className="text-[10px] font-mono text-foreground/50">
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                        </div>
                        <div className="text-sm text-foreground/90 whitespace-pre-wrap mt-1">{n.body}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}


export default function InterviewSession() {
    const { iid } = useParams();
    const { user, logout } = useAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [bundle, setBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("qa");          // qa | rating | feedback
    const [newQuestion, setNewQuestion] = useState("");
    const [addBusy, setAddBusy] = useState(false);

    const load = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get(`/interviewer/interview/${iid}`);
            setBundle(data);
        } catch (err) {
            toast.error(formatApiError(err));
            if (err?.response?.status === 403 || err?.response?.status === 404) {
                setTimeout(() => nav("/interviewer"), 1500);
            }
        } finally { if (!silent) setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [iid]);

    // Silent refresh — does NOT toggle the page-level loading state so the
    // resume iframe (and all other DOM nodes) stay mounted. Used after any
    // right-panel Save so we never re-mount the iframe.
    const refresh = () => load({ silent: true });

    const saveAnswer = async (qid, patch) => {
        try {
            await api.patch(`/interviewer/interview/${iid}/qa/${qid}`, patch);
            await refresh();
            toast.success("Answer saved");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveRating = async (payload) => {
        try {
            await api.post(`/interviewer/interview/${iid}/rating`, payload);
            await refresh();
            toast.success("Rating saved");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveFeedback = async (payload) => {
        try {
            await api.patch(`/interviewer/interview/${iid}/feedback`, payload);
            await refresh();
            toast.success("Feedback saved");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const addQuestion = async () => {
        if (!newQuestion.trim()) return;
        setAddBusy(true);
        try {
            await api.post(`/interviewer/interview/${iid}/qa`, { question: newQuestion.trim() });
            setNewQuestion("");
            await refresh();
            toast.success("Question added");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setAddBusy(false); }
    };

    const [closingBusy, setClosingBusy] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const closeInterview = async () => {
        setClosingBusy(true);
        try {
            await api.post(`/interviewer/interview/${iid}/close`);
            toast.success("Interview closed");
            setCloseOpen(false);
            nav("/interviewer");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setClosingBusy(false);
        }
    };

    // has_resume is derived above the loading early-return so the value is
    // stable across the first transition (no extra ResumePanel re-render).
    const has_resume = bundle?.has_resume || false;

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="text-sm text-muted-foreground font-mono">Loading session…</div></div>;
    if (!bundle) return null;

    const { interview, candidate, job, qa, rating, resume_filename, visibility, rating_skills_template } = bundle;
    const resumeType = visibility?.resume_type || "masked";
    const aiVisible = !!visibility?.ai_visible;
    // Merge recruiter-pushed skills with candidate's own skill chips.
    const skillsForRating = {
        hard: (rating_skills_template?.hard_skills?.length ? rating_skills_template.hard_skills : (candidate.skills || [])),
        soft: rating_skills_template?.soft_skills || [],
        critical: rating_skills_template?.critical || [],
    };

    return (
        <div className="min-h-screen bg-background" data-testid="interview-session-page">
            {/* Lean header — fixes the two-headers bug (no AppShell wrap) */}
            <header className="border-b border-border bg-white sticky top-0 z-30">
                <div className="flex items-center h-16 px-4 sm:px-6 gap-4">
                    <Link to="/interviewer" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-ikb" data-testid="iv-back">
                        <ArrowLeft className="w-3.5 h-3.5" /> Back
                    </Link>
                    <div className="flex items-center gap-2">
                        <BrandLogo size={24} />
                        <div className="flex flex-col leading-tight">
                            <span className="font-display font-black tracking-tight text-sm">{branding.short_name}</span>
                            <span className="text-[10px] uppercase tracking-widest font-mono text-ikb">Interviewer · Session</span>
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        <Button onClick={() => setCloseOpen(true)} disabled={closingBusy} className="rounded-none h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="close-interview-btn">
                            {closingBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            {closingBusy ? "Closing…" : "Close interview"}
                        </Button>
                        <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
                            <AlertDialogContent className="rounded-none" data-testid="close-interview-dialog">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Close this interview round?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        It will be removed from your upcoming list. You won't be able to edit your feedback,
                                        Q&amp;A or ratings after this. The recruiter will see your round as completed.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-none" data-testid="close-interview-cancel">Keep open</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={closeInterview}
                                        disabled={closingBusy}
                                        className="rounded-none bg-emerald-600 hover:bg-emerald-700"
                                        data-testid="close-interview-confirm"
                                    >
                                        {closingBusy ? "Closing…" : "Yes, close interview"}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                        <div className="hidden md:flex items-center gap-2 text-xs">
                            <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                {(user?.name || "").slice(0, 2).toUpperCase()}
                            </div>
                            <div className="leading-tight">
                                <div className="font-medium">{user?.name}</div>
                                <div className="text-[10px] uppercase tracking-widest text-foreground/70">Interviewer</div>
                            </div>
                        </div>
                        <Button variant="outline" className="rounded-none h-9" onClick={async () => { await logout(); nav("/login"); }} data-testid="iv-logout">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4">
                {/* Candidate strip — sanitized: no contact info */}
                <div className="border border-border bg-white p-4 sm:p-5" data-testid="candidate-strip">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Candidate</div>
                            <h1 className="font-display font-black text-2xl sm:text-3xl mt-1 flex items-center gap-2">
                                <User2 className="w-6 h-6 text-ikb" /> {candidate.name}
                            </h1>
                            <div className="flex items-center gap-3 text-xs font-mono text-foreground/80 mt-2 flex-wrap">
                                {candidate.current_title && <span>{candidate.current_title}</span>}
                                {candidate.current_company && <><span>·</span><span>{candidate.current_company}</span></>}
                                {candidate.total_experience_years != null && <><span>·</span><span>{candidate.total_experience_years} yrs experience</span></>}
                                {candidate.location && <><span>·</span><span>{candidate.location}</span></>}
                            </div>
                            {candidate.skills?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="candidate-skills">
                                    {candidate.skills.slice(0, 16).map((s) => (
                                        <span key={s} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-secondary px-2 py-0.5">{s}</span>
                                    ))}
                                    {candidate.skills.length > 16 && <span className="text-[10px] font-mono text-foreground/40">+{candidate.skills.length - 16} more</span>}
                                </div>
                            )}
                        </div>
                        <div className="bg-secondary/50 border border-border p-3 min-w-[260px]">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1">Round</div>
                            <div className="font-display font-bold text-base">{interview.round_name || `Round ${interview.round_number || 1}`}</div>
                            <div className="flex items-center gap-2 text-xs font-mono text-foreground/80 mt-2">
                                <Clock className="w-3.5 h-3.5" /> {fmtTime(interview.scheduled_at, interview.scheduled_date_only)}
                            </div>
                            <div className="flex items-center gap-2 text-xs font-mono text-foreground/80 mt-1.5">
                                <ModeIcon mode={interview.mode} /> {interview.mode || "Video"} · {interview.duration_minutes || 60} min
                            </div>
                            {job?.title && (
                                <div className="flex items-center gap-2 text-xs font-mono text-foreground/80 mt-1.5">
                                    <Briefcase className="w-3.5 h-3.5" /> {job.title}
                                </div>
                            )}
                            {interview.meet_link && (
                                <a href={interview.meet_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest border border-ikb bg-blue-50 text-ikb px-2 py-1.5 hover:bg-blue-100" data-testid="meet-link">
                                    <ExternalLink className="w-3 h-3" /> Join meeting
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* AI insight panel — only visible when the admin has unlocked
                    AI analysis for this interviewer (company default or override) */}
                {aiVisible && (candidate.ai_summary || candidate.ai_analysis || candidate.screening_profile) && (
                    <div className="border border-ikb/40 bg-blue-50/40 p-4 sm:p-5" data-testid="ai-insight-panel">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ikb mb-2 inline-flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> AI insight · unlocked for your account
                        </div>
                        {candidate.ai_summary && (
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap mb-3" data-testid="ai-summary">
                                {typeof candidate.ai_summary === "string" ? candidate.ai_summary : JSON.stringify(candidate.ai_summary)}
                            </div>
                        )}
                        {candidate.screening_profile && (
                            <details className="mt-2">
                                <summary className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 cursor-pointer">Screening profile</summary>
                                <pre className="text-[11px] font-mono bg-white border border-border p-2 mt-1 overflow-auto max-h-48 whitespace-pre-wrap" data-testid="ai-screening">{JSON.stringify(candidate.screening_profile, null, 2)}</pre>
                            </details>
                        )}
                        {candidate.ai_analysis && (
                            <details className="mt-2">
                                <summary className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 cursor-pointer">Raw analysis</summary>
                                <pre className="text-[11px] font-mono bg-white border border-border p-2 mt-1 overflow-auto max-h-48 whitespace-pre-wrap" data-testid="ai-analysis">{JSON.stringify(candidate.ai_analysis, null, 2)}</pre>
                            </details>
                        )}
                    </div>
                )}

                {/* Resume + working panels */}
                <div className="grid lg:grid-cols-[1fr_1fr] gap-4 items-start">
                    {/* Resume panel — memoized; fetches blob via authenticated axios */}
                    <ResumePanel iid={iid} filename={resume_filename} hasResume={has_resume} resumeType={resumeType} />

                    {/* Right column: tabs */}
                    <div className="space-y-4">
                        <div className="inline-flex border border-border bg-white" data-testid="session-tabs">
                            <button onClick={() => setTab("qa")} className={`px-3 py-2 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${tab === "qa" ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid="tab-qa"><MessageSquare className="w-3.5 h-3.5" /> Q&A ({qa.length})</button>
                            <button onClick={() => setTab("rating")} className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border inline-flex items-center gap-1.5 ${tab === "rating" ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid="tab-rating"><Star className="w-3.5 h-3.5" /> Rating</button>
                            <button onClick={() => setTab("feedback")} className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border inline-flex items-center gap-1.5 ${tab === "feedback" ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid="tab-feedback"><CheckCircle2 className="w-3.5 h-3.5" /> Feedback</button>
                            <button onClick={() => setTab("notes")} className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border inline-flex items-center gap-1.5 ${tab === "notes" ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid="tab-notes"><StickyNote className="w-3.5 h-3.5" /> Notes</button>
                        </div>

                        {tab === "qa" && (
                            <div className="space-y-3" data-testid="qa-list">
                                {qa.length === 0 && (
                                    <div className="border border-dashed border-border bg-white p-8 text-center text-sm text-foreground/60" data-testid="qa-empty">
                                        No questions yet — add one below to get started.
                                    </div>
                                )}
                                {qa.map((q) => <QACard key={q.id} q={q} onSave={saveAnswer} />)}
                                <div className="border border-border bg-white p-3 flex items-center gap-2" data-testid="qa-add">
                                    <Input
                                        value={newQuestion}
                                        onChange={(e) => setNewQuestion(e.target.value)}
                                        placeholder="Add your own question…"
                                        className="rounded-none h-10 text-sm flex-1"
                                        data-testid="qa-add-input"
                                    />
                                    <Button onClick={addQuestion} disabled={addBusy || !newQuestion.trim()} className="rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="qa-add-btn">
                                        {addBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {tab === "rating" && <RatingPanel skills={skillsForRating} rating={rating} onSave={saveRating} />}
                        {tab === "feedback" && <FeedbackBlock interview={interview} onSave={saveFeedback} />}
                        {tab === "notes" && <InterviewerNotesPanel iid={iid} />}
                    </div>
                </div>
            </main>
        </div>
    );
}
